import React, { useState, Fragment } from 'react';
import { SearchIcon, FilterIcon, DownloadIcon, ChevronDownIcon, ChevronRightIcon, CalendarIcon, ClockIcon, UserIcon, ShieldIcon, FileTextIcon, ListIcon, ActivityIcon } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
export function AuditLogsPage() {
  const {
    auditLogs
  } = useAppContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'timeline'>('table');
  const categories = ['all', 'Approvals', 'Reassignments', 'Budget Overrides', 'Ticket Issuance', 'Refunds', 'Delegations', 'System'];
  const filteredLogs = auditLogs.filter(log => {
    const matchesSearch = searchQuery === '' || log.user.toLowerCase().includes(searchQuery.toLowerCase()) || log.action.toLowerCase().includes(searchQuery.toLowerCase()) || log.details.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || log.category === filterCategory;
    return matchesSearch && matchesCategory;
  });
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Approvals':
        return 'bg-emerald-100 text-emerald-700';
      case 'Reassignments':
        return 'bg-blue-100 text-blue-700';
      case 'Budget Overrides':
        return 'bg-amber-100 text-amber-700';
      case 'Ticket Issuance':
        return 'bg-purple-100 text-purple-700';
      case 'Refunds':
        return 'bg-red-100 text-red-700';
      case 'Delegations':
        return 'bg-indigo-100 text-indigo-700';
      case 'System':
        return 'bg-slate-100 text-slate-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };
  return <div className="space-y-6" data-id="element-433">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-434">
        <div data-id="element-435">
          <h2 className="text-2xl font-bold text-navy-900" data-id="element-436">Audit Logs</h2>
          <p className="text-slate-500" data-id="element-437">
            Complete audit trail of all system actions and changes.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-438">
          <button className="flex items-center rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100 transition-colors" data-id="element-439">
            <DownloadIcon className="mr-2 h-4 w-4" data-id="element-440" />
            Export CSV
          </button>
          <button className="flex items-center rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100 transition-colors" data-id="element-441">
            <FileTextIcon className="mr-2 h-4 w-4" data-id="element-442" />
            Export PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-id="element-443">
        <div className="flex flex-col sm:flex-row gap-4" data-id="element-444">
          <div className="relative flex-1" data-id="element-445">
            <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" data-id="element-446" />
            <input type="text" placeholder="Search by user, action, or details..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="h-10 w-full rounded-lg border border-slate-300 bg-slate-50 pl-10 pr-4 text-sm focus:border-navy-600 focus:outline-none focus:ring-1 focus:ring-navy-600" data-id="element-447" />
          </div>
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="h-10 rounded-lg border border-slate-300 bg-white px-4 text-sm focus:border-navy-600 focus:outline-none" data-id="element-448">
            {categories.map(cat => <option key={cat} value={cat} data-id="element-449">
                {cat === 'all' ? 'All Categories' : cat}
              </option>)}
          </select>
          <input type="date" className="h-10 rounded-lg border border-slate-300 bg-white px-4 text-sm focus:border-navy-600 focus:outline-none" data-id="element-450" />
          <div className="flex rounded-lg border border-slate-300 overflow-hidden" data-id="element-451">
            <button onClick={() => setViewMode('table')} className={`px-3 py-2 text-sm font-medium transition-colors ${viewMode === 'table' ? 'bg-navy-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-id="element-452">
              <ListIcon className="h-4 w-4" data-id="element-453" />
            </button>
            <button onClick={() => setViewMode('timeline')} className={`px-3 py-2 text-sm font-medium transition-colors ${viewMode === 'timeline' ? 'bg-navy-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-id="element-454">
              <ActivityIcon className="h-4 w-4" data-id="element-455" />
            </button>
          </div>
        </div>
      </div>

      {/* Results count */}
      <p className="text-sm text-slate-500" data-id="element-456">
        {filteredLogs.length} log entries found
      </p>

      {viewMode === 'table' /* Table View */ ? <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-id="element-457">
          <div className="overflow-x-auto" data-id="element-458">
            <table className="w-full text-left text-sm" data-id="element-459">
              <thead className="bg-slate-50 text-slate-500" data-id="element-460">
                <tr data-id="element-461">
                  <th className="px-4 py-3 font-medium w-8" data-id="element-462"></th>
                  <th className="px-4 py-3 font-medium" data-id="element-463">Timestamp</th>
                  <th className="px-4 py-3 font-medium" data-id="element-464">User</th>
                  <th className="px-4 py-3 font-medium" data-id="element-465">Action</th>
                  <th className="px-4 py-3 font-medium" data-id="element-466">Category</th>
                  <th className="px-4 py-3 font-medium" data-id="element-467">IP Address</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-468">
                {filteredLogs.map(log => <Fragment key={log.id} data-id="element-469">
                    <tr className="hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)} data-id="element-470">
                      <td className="px-4 py-3" data-id="element-471">
                        {expandedRow === log.id ? <ChevronDownIcon className="h-4 w-4 text-slate-400" data-id="element-472" /> : <ChevronRightIcon className="h-4 w-4 text-slate-400" data-id="element-473" />}
                      </td>
                      <td className="px-4 py-3 text-slate-600 font-mono text-xs whitespace-nowrap" data-id="element-474">
                        {log.timestamp}
                      </td>
                      <td className="px-4 py-3" data-id="element-475">
                        <div className="flex items-center gap-2" data-id="element-476">
                          <div className="h-6 w-6 rounded-full bg-navy-100 flex items-center justify-center flex-shrink-0" data-id="element-477">
                            <UserIcon className="h-3 w-3 text-navy-600" data-id="element-478" />
                          </div>
                          <span className="font-medium text-slate-900 text-xs" data-id="element-479">
                            {log.user}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-700 text-xs max-w-xs truncate" data-id="element-480">
                        {log.action}
                      </td>
                      <td className="px-4 py-3" data-id="element-481">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${getCategoryColor(log.category)}`} data-id="element-482">
                          {log.category}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-500 font-mono text-xs" data-id="element-483">
                        {log.ip}
                      </td>
                    </tr>
                    {expandedRow === log.id && <tr data-id="element-484">
                        <td colSpan={6} className="px-4 py-4 bg-slate-50" data-id="element-485">
                          <div className="rounded-lg bg-white border border-slate-200 p-4" data-id="element-486">
                            <h4 className="text-xs font-bold text-slate-700 uppercase mb-2" data-id="element-487">
                              Full Details
                            </h4>
                            <p className="text-sm text-slate-600 mb-3" data-id="element-488">
                              {log.details}
                            </p>
                            <div className="grid grid-cols-3 gap-4 text-xs" data-id="element-489">
                              <div data-id="element-490">
                                <span className="text-slate-400 block" data-id="element-491">
                                  Log ID
                                </span>
                                <span className="font-mono text-slate-700" data-id="element-492">
                                  {log.id}
                                </span>
                              </div>
                              <div data-id="element-493">
                                <span className="text-slate-400 block" data-id="element-494">
                                  IP Address
                                </span>
                                <span className="font-mono text-slate-700" data-id="element-495">
                                  {log.ip}
                                </span>
                              </div>
                              <div data-id="element-496">
                                <span className="text-slate-400 block" data-id="element-497">
                                  Category
                                </span>
                                <span className="text-slate-700" data-id="element-498">
                                  {log.category}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>}
                  </Fragment>)}
              </tbody>
            </table>
          </div>
        </div> /* Timeline View */ : <div className="space-y-0" data-id="element-499">
          {filteredLogs.map((log, idx) => <div key={log.id} className="flex gap-4" data-id="element-500">
              <div className="flex flex-col items-center" data-id="element-501">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${getCategoryColor(log.category)}`} data-id="element-502">
                  <ShieldIcon className="h-4 w-4" data-id="element-503" />
                </div>
                {idx < filteredLogs.length - 1 && <div className="w-0.5 flex-1 bg-slate-200 my-1" data-id="element-504" />}
              </div>
              <div className="flex-1 pb-6" data-id="element-505">
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-id="element-506">
                  <div className="flex items-center justify-between mb-1" data-id="element-507">
                    <span className="font-semibold text-sm text-slate-900" data-id="element-508">
                      {log.action}
                    </span>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${getCategoryColor(log.category)}`} data-id="element-509">
                      {log.category}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 mb-2" data-id="element-510">{log.details}</p>
                  <div className="flex items-center gap-4 text-xs text-slate-400" data-id="element-511">
                    <span className="flex items-center gap-1" data-id="element-512">
                      <UserIcon className="h-3 w-3" data-id="element-513" />
                      {log.user}
                    </span>
                    <span className="flex items-center gap-1" data-id="element-514">
                      <ClockIcon className="h-3 w-3" data-id="element-515" />
                      {log.timestamp}
                    </span>
                    <span className="font-mono" data-id="element-516">{log.ip}</span>
                  </div>
                </div>
              </div>
            </div>)}
        </div>}
    </div>;
}